import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, lazy, Suspense } from 'react'
import gsap from 'gsap'
import { motion } from 'framer-motion'
import { useTranslation } from '../hooks/useTranslation'
import { useHydration } from '../contexts/HydrationContext'
import Counter from './Counter'
import NeuralInteractiveLayer from './NeuralInteractiveLayer'

const ParticlesWrapper = lazy(
    () => import(/* webpackChunkName: "particles" */ './ParticlesWrapper')
)

const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0 },
}

const ANIMATION_DELAY_MS = 800

const BadanibexHero: React.FC = () => {
    const { t, isRTL } = useTranslation()
    const isHydrated = useHydration()
    const sectionRef = useRef<HTMLElement>(null)
    const dynamicTextRef = useRef<HTMLSpanElement>(null)

    const [index, setIndex] = useState(0)
    const [displayIndex, setDisplayIndex] = useState(0)
    const [commandText, setCommandText] = useState('')
    const [allowAnimations, setAllowAnimations] = useState(false)
    const [isHeroVisible, setIsHeroVisible] = useState(false)
    const [particlesMounted, setParticlesMounted] = useState(false)
    const [isClientMounted, setIsClientMounted] = useState(false)

    useEffect(() => { setIsClientMounted(true) }, [])

    useEffect(() => {
        if (!isClientMounted) return
        const el = sectionRef.current
        if (!el) return
        const io = new IntersectionObserver(
            entries => {
                const [entry] = entries
                if (entry) setIsHeroVisible(entry.isIntersecting && entry.intersectionRatio >= 0.6)
            },
            { threshold: 0.6 }
        )
        io.observe(el)
        return () => io.disconnect()
    }, [isClientMounted])

    useEffect(() => {
        if (isClientMounted && isHeroVisible && !particlesMounted) {
            setParticlesMounted(true)
        }
    }, [isClientMounted, isHeroVisible, particlesMounted])

    const shouldMountParticles = isClientMounted && isHeroVisible && particlesMounted

    useEffect(() => {
        if (!isHydrated || !isClientMounted) return
        const tId = window.setTimeout(() => setAllowAnimations(true), ANIMATION_DELAY_MS)
        return () => window.clearTimeout(tId)
    }, [isHydrated, isClientMounted])

    useEffect(() => {
        if (!allowAnimations || !isClientMounted) return
        const el = sectionRef.current
        if (!el) return
        let rafPending = false
        let lastX = 0
        let lastY = 0
        const move = (e: MouseEvent) => {
            lastX = e.clientX
            lastY = e.clientY
            if (rafPending) return
            rafPending = true
            requestAnimationFrame(() => {
                const cx = window.innerWidth / 2
                const cy = window.innerHeight / 2
                el.style.setProperty('--card-rotate-x', `${((lastY - cy) / cy) * -1.2}deg`)
                el.style.setProperty('--card-rotate-y', `${((lastX - cx) / cx) * 1.2}deg`)
                rafPending = false
            })
        }
        const leave = () => {
            el.style.setProperty('--card-rotate-x', '0deg')
            el.style.setProperty('--card-rotate-y', '0deg')
        }
        window.addEventListener('mousemove', move, { passive: true })
        window.addEventListener('mouseleave', leave)
        return () => {
            window.removeEventListener('mousemove', move)
            window.removeEventListener('mouseleave', leave)
        }
    }, [allowAnimations, isClientMounted])

    const commands = useMemo(() => [
        'GET /api/v1/products/ready-to-deploy',
        'sh build-store.sh --template=react-19',
        'php artisan migrate:fresh --seed',
        'npm run build:production --success',
    ], [])

    useEffect(() => {
        if (!allowAnimations || !isClientMounted) return
        let charIndex = 0
        let commandIndex = 0
        let timeoutId: NodeJS.Timeout
        const typeEffect = setInterval(() => {
            const currentFullCommand = commands[commandIndex]
            setCommandText(currentFullCommand.substring(0, charIndex))
            charIndex++
            if (charIndex > currentFullCommand.length) {
                timeoutId = setTimeout(() => {
                    charIndex = 0
                    commandIndex = (commandIndex + 1) % commands.length
                }, 2000)
            }
        }, 100)
        const slideTimer = setInterval(() => setIndex(prev => (prev + 1) % 3), 5000)
        return () => {
            clearInterval(typeEffect)
            clearInterval(slideTimer)
            if (timeoutId) clearTimeout(timeoutId)
        }
    }, [allowAnimations, isClientMounted, commands])

    // Set initial hidden state before paint to prevent flash
    useLayoutEffect(() => {
        const el = dynamicTextRef.current
        if (!el) return
        gsap.set(el, { yPercent: 100, opacity: 0 })
    }, [])

    // GSAP clip-reveal when index changes
    useEffect(() => {
        const el = dynamicTextRef.current
        if (!el) return
        setDisplayIndex(index)
        gsap.fromTo(
            el,
            { yPercent: 100, opacity: 0, skewX: isRTL ? 2 : -2 },
            { yPercent: 0, opacity: 1, skewX: 0, duration: 0.6, ease: 'power3.out' }
        )
    }, [index, isRTL])

    return (
        <section
            ref={sectionRef}
            className="relative min-h-[90vh] lg:min-h-screen flex flex-col justify-center overflow-hidden transition-colors duration-500"
            style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
        >
            {/* Laser scan line */}
            {allowAnimations && (
                <motion.div
                    animate={{ y: ['0%', '1000%'] }}
                    transition={{ repeat: Infinity, duration: 10, ease: 'linear' }}
                    className="hidden sm:block absolute inset-x-0 h-[2px] z-10 pointer-events-none"
                    style={{
                        background: 'linear-gradient(to right, transparent, color-mix(in srgb, var(--gold) 30%, transparent), transparent)'
                    }}
                />
            )}

            {allowAnimations && (
                <div className="hidden sm:block">
                    <NeuralInteractiveLayer />
                </div>
            )}

            {shouldMountParticles && (
                <Suspense fallback={null}>
                    <ParticlesWrapper />
                </Suspense>
            )}

            <div className="container mx-auto px-4 sm:px-6 relative z-30 flex flex-col lg:flex-row items-center gap-8 lg:gap-12 pt-6 lg:pt-0">

                {/* ── Text side ── */}
                <motion.div
                    initial={{ x: isRTL ? 50 : -50, opacity: 1 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                    {/* Badge */}
                    <div
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-4 sm:mb-6 tracking-widest uppercase"
                        style={{
                            border: '1px solid color-mix(in srgb, var(--gold) 20%, transparent)',
                            backgroundColor: 'color-mix(in srgb, var(--gold) 10%, transparent)',
                            color: 'var(--gold)',
                        }}
                    >
                        <span className="relative flex h-2 w-2">
                            <span
                                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                                style={{ backgroundColor: 'var(--gold)' }}
                            />
                            <span
                                className="relative inline-flex rounded-full h-2 w-2"
                                style={{ backgroundColor: 'var(--gold)' }}
                            />
                        </span>
                        {t('main.hero.badge1')}
                    </div>

                    {/* Heading block */}
                    <div className={`flex flex-col ${isRTL ? 'gap-5 mb-6' : 'gap-3 mb-8'}`}>

                        {/* Static H1 */}
                        <h1
                            className={`font-black tracking-tight ${
                                isRTL
                                    ? 'text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.35]'
                                    : 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1]'
                            }`}
                            style={{ color: 'var(--text)' }}
                            data-lcp
                        >
                            {t('main.hero.staticTitle')}
                        </h1>

                        {/* Dynamic rotating text — GSAP clip reveal */}
                        <div
                            className="overflow-hidden"
                            style={{ lineHeight: isRTL ? '1.6' : '1.3', paddingBottom: isRTL ? '6px' : '2px' }}
                        >
                            <span
                                ref={dynamicTextRef}
                                className={`inline-block font-extrabold text-transparent bg-clip-text ${
                                    isRTL
                                        ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl'
                                        : 'text-xl sm:text-2xl md:text-3xl lg:text-4xl'
                                }`}
                                style={{
                                    backgroundImage: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-hover) 50%, var(--gold) 100%)',
                                    willChange: 'transform, opacity',
                                }}
                            >
                                {t(`main.hero.slides.${displayIndex}.title`)}
                            </span>
                        </div>
                    </div>

                    {/* Description */}
                    <p
                        className={`text-base sm:text-lg max-w-xl mb-6 sm:mb-8 ${isRTL ? 'leading-[1.8]' : 'leading-relaxed'}`}
                        style={{ color: 'var(--text-2)' }}
                    >
                        {t('main.hero.slides.0.subtitle')}
                    </p>

                    {/* CTAs */}
                    <div className="flex flex-wrap gap-4">
                        <motion.a
                            href="/catalog"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative overflow-hidden px-8 py-4 font-bold rounded-xl shadow-lg inline-flex items-center justify-center cursor-pointer"
                            style={{
                                backgroundColor: 'var(--gold)',
                                color: 'var(--bg)',
                                boxShadow: '0 10px 25px -5px color-mix(in srgb, var(--gold) 30%, transparent)',
                            }}
                        >
                            <motion.div
                                animate={{ x: ['-100%', '200%'] }}
                                transition={{ repeat: Infinity, duration: 3, ease: 'linear' }}
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 pointer-events-none"
                            />
                            <span className="relative z-10">{t('main.hero.ctaPrimary')}</span>
                        </motion.a>
                        <motion.a
                            href="/start-project"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative overflow-hidden p-[1px] rounded-xl inline-block"
                            style={{ background: 'var(--border)' }}
                        >
                            <span
                                className="relative z-10 flex px-8 py-4 rounded-[11px] font-bold"
                                style={{ backgroundColor: 'var(--surface)', color: 'var(--text)' }}
                            >
                                {t('main.hero.cta')}
                            </span>
                            <motion.div
                                animate={{ x: ['-100%', '200%'] }}
                                transition={{ repeat: Infinity, duration: 4, ease: 'linear' }}
                                className="absolute inset-0 z-20 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 pointer-events-none"
                            />
                        </motion.a>
                    </div>

                    {/* Stats */}
                    <motion.div
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.3, staggerChildren: 0 }}
                        className="grid grid-cols-2 gap-4 sm:gap-8 pt-8 sm:pt-10 mt-8 sm:mt-10"
                        style={{ borderTop: '1px solid var(--border)' }}
                    >
                        <div className="flex flex-col gap-1">
                            <div
                                className="text-2xl sm:text-3xl md:text-4xl font-black flex items-center gap-1"
                                style={{ color: 'var(--text)' }}
                            >
                                <span style={{ color: 'var(--gold)' }}>+</span>
                                <Counter value={53} />
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--text-2)' }}>
                                {t('main.hero.stats.digitalAssets')}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <div
                                className="text-2xl sm:text-3xl md:text-4xl font-black flex items-center gap-1"
                                style={{ color: 'var(--text)' }}
                                dir="ltr"
                            >
                                {isRTL ? (
                                    <span>99<span style={{ color: 'var(--gold)' }}>.9%</span></span>
                                ) : (
                                    <><Counter value={99} /><span style={{ color: 'var(--gold)' }}>.9%</span></>
                                )}
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: 'var(--text-2)' }}>
                                {t('main.hero.stats.serverUptime')}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* ── Terminal card ── */}
                <motion.div
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1 }}
                    className="hidden sm:block flex-1 w-full max-w-xl relative"
                >
                    <div
                        className="transform-gpu lg:rotate-2 hover:rotate-0 transition-transform duration-500 rounded-3xl p-[3px] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)]"
                        style={{
                            background: 'linear-gradient(to bottom, rgba(255,255,255,0.2), rgba(255,255,255,0.05))',
                        }}
                    >
                        <div
                            className="relative rounded-[23px] overflow-hidden isolate"
                            style={{
                                backgroundImage: 'url(/cover.jpg)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: 'var(--surface)',
                            }}
                            dir="ltr"
                        >
                            {/* Overlay */}
                            <div className="absolute -inset-px pointer-events-none bg-gradient-to-b from-black/85 via-black/80 to-black/90" />

                            {/* Title bar */}
                            <div className="relative z-10 px-6 py-3.5 flex items-center justify-between bg-black/60 border-b border-white/10 backdrop-blur-md">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                                    <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                                </div>
                                <span className="text-[11px] font-mono tracking-widest uppercase text-white/50 font-medium">
                                    system_v1.2_BadanIbex®
                                </span>
                            </div>

                            {/* Terminal body */}
                            <div className="relative z-10 p-7 sm:p-8 font-mono text-sm sm:text-base min-h-[280px]">
                                <div className="flex items-start gap-3 mb-8">
                                    <span className="font-bold" style={{ color: 'var(--gold)' }}>➜</span>
                                    <span className="whitespace-pre-wrap text-white font-semibold tracking-wide drop-shadow-md flex-1">
                                        {commandText}
                                    </span>
                                    <motion.span
                                        animate={{ opacity: [1, 0] }}
                                        transition={{ repeat: Infinity, duration: 0.8 }}
                                        className="w-2.5 h-6 inline-block rounded-sm"
                                        style={{
                                            backgroundColor: 'var(--gold)',
                                            boxShadow: '0 0 8px color-mix(in srgb, var(--gold) 80%, transparent)',
                                        }}
                                    />
                                </div>

                                <div className="space-y-4 text-xs sm:text-sm pt-2 border-t border-white/10">
                                    {[
                                        { label: 'Deployment Status', value: 'PRODUCTION_READY', badge: true },
                                        { label: 'Backend Framework', value: 'Laravel 12.x', suffix: '(LTS)', badge: false },
                                        { label: 'Frontend Engine', value: 'React 19 + Framer', badge: false },
                                    ].map(row => (
                                        <div
                                            key={row.label}
                                            className="flex justify-between items-center py-2 px-1 hover:bg-white/5 rounded-lg transition-colors"
                                        >
                                            <span className="text-gray-300/90 font-medium">{row.label}</span>
                                            {row.badge ? (
                                                <span
                                                    className="px-3 py-1 font-mono text-xs font-bold rounded-md border"
                                                    style={{
                                                        backgroundColor: 'color-mix(in srgb, var(--gold) 12%, transparent)',
                                                        color: 'var(--gold)',
                                                        borderColor: 'color-mix(in srgb, var(--gold) 30%, transparent)',
                                                    }}
                                                >
                                                    {row.value}
                                                </span>
                                            ) : (
                                                <span className="font-semibold text-white">
                                                    {row.value}{' '}
                                                    {row.suffix && <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{row.suffix}</span>}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Corner glows */}
            <div
                className="absolute top-1/4 -right-20 w-96 h-96 rounded-full blur-[120px] pointer-events-none"
                style={{ backgroundColor: 'var(--gold)', opacity: 0.07 }}
            />
            <div
                className="absolute bottom-1/4 -left-20 w-96 h-96 rounded-full blur-[120px] pointer-events-none"
                style={{ backgroundColor: 'color-mix(in srgb, var(--cyan) 10%, transparent)' }}
            />
        </section>
    )
}

export default BadanibexHero